import { Controller } from "@hotwired/stimulus"
import { createAlert, clearAlerts } from '../scripts/createAlert';

let intervalId;

// Placeholder used while protecting periods that are not sentence endings.
// Must be one character so string indexes still match the original transcript.
const PERIOD_PLACEHOLDER = '\uFFFF';
const SPEAKER_LABEL_PATTERN = /^(Interviewer:|Respondent:)(\s*)/;

const TITLE_ABBREVIATIONS = [
  'Mr', 'Mrs', 'Ms', 'Mz', 'Dr', 'Prof', 'Sr', 'Jr', 'vs', 'etc', 'approx',
  'Dept', 'Univ', 'Assn', 'Inc', 'Ltd', 'Co', 'Corp', 'St', 'Ave', 'Blvd',
  'Rd', 'Mt', 'Ft', 'Gen', 'Col', 'Capt', 'Sgt', 'Rev', 'Hon', 'Gov', 'Pres',
  'Sen', 'Rep', 'No', 'Fig', 'Vol', 'pp', 'cf', 'al', 'Jan', 'Feb', 'Mar',
  'Apr', 'Jun', 'Jul', 'Aug', 'Sep', 'Sept', 'Oct', 'Nov', 'Dec', 'Mon',
  'Tue', 'Tues', 'Wed', 'Thu', 'Thur', 'Thurs', 'Fri', 'Sat', 'Sun'
];

const MULTI_PART_ABBREVIATIONS = [
  'Ph.D', 'M.D', 'B.A', 'B.S', 'M.A', 'M.S', 'D.D.S', 'D.O', 'J.D', 'LL.D',
  'U.S.A', 'U.S', 'U.K', 'E.U', 'D.C', 'a.m', 'p.m', 'A.M', 'P.M'
];

export default class extends Controller {
  static targets = [ 'transcript' ]
  static values = {
    teamId: Number,
    gameId: Number,
    quotes: Array
  }

  originalTranscriptText = '';
  savedQuoteFromDragSelection = false;
  activeSentenceIndex = 0;
  keyboardSentenceHighlightActive = false;

  connect() {
    // if we are waiting for transcription to be ready, we will poll the server every 5 seconds
    if (!this.hasTranscriptTarget) {
      intervalId = setInterval( () => this.fetchTranscription(), 5000);
      return;
    }

    this.originalTranscriptText = this.transcriptTarget.textContent;
    this.transcriptTarget.tabIndex = 0;
    this.renderTranscript();

    this.transcriptTarget.addEventListener('mouseup', (event) => {
      this.handleMouseUp(event);
    });

    this.transcriptTarget.addEventListener('click', (event) => {
      this.handleClick(event);
    });

    this.transcriptTarget.addEventListener('keydown', (event) => {
      this.handleKeydown(event);
    });

    this.transcriptTarget.addEventListener('blur', () => {
      this.hideKeyboardSentenceHighlight();
    });
  }

  fetchTranscription() {
    try {
      fetch(`/entity/teams/${this.teamIdValue}/games/${this.gameIdValue}/transcription_results`).then(results => {
        if (results.status === 200) {
          clearInterval(intervalId);
          window.location.reload();
        }
      });
    } catch (e) {
      console.error('Error when trying to fetch transcription: ', e);
    }
  }

  handleMouseUp(event) {
    const selectedText = this.getSelectedText();

    // Drag-to-select still creates a quote from the highlighted text
    if (selectedText) {
      this.savedQuoteFromDragSelection = true;
      this.saveQuote(selectedText);
      window.getSelection()?.removeAllRanges();
    }
  }

  handleClick(event) {
    // mouseup + click both fire after a drag; skip the click so we do not also quote the sentence
    if (this.savedQuoteFromDragSelection) {
      this.savedQuoteFromDragSelection = false;
      return;
    }

    const quoteElement = event.target.closest('.quote');
    if (quoteElement) {
      this.deleteQuote(quoteElement.dataset.quoteId);
      return;
    }

    const sentenceElement = event.target.closest('.sentence');
    if (!sentenceElement) {
      return;
    }

    this.selectSentenceAsQuote(sentenceElement);
  }

  handleKeydown(event) {
    const sentenceElements = this.sentenceElements();
    if (sentenceElements.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.setActiveSentence(Math.min(this.activeSentenceIndex + 1, sentenceElements.length - 1), { showHighlight: true });
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setActiveSentence(Math.max(this.activeSentenceIndex - 1, 0), { showHighlight: true });
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.setActiveSentence(0, { showHighlight: true });
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.setActiveSentence(sentenceElements.length - 1, { showHighlight: true });
      return;
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      const activeSentenceElement = sentenceElements[this.activeSentenceIndex];
      const quoteId = this.quoteIdForSentence(activeSentenceElement);
      if (quoteId) {
        event.preventDefault();
        this.deleteQuote(quoteId);
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      const activeSentenceElement = sentenceElements[this.activeSentenceIndex];
      if (!activeSentenceElement) {
        return;
      }

      event.preventDefault();
      this.handleEnterOnActiveSentence(activeSentenceElement);
    }
  }

  handleEnterOnActiveSentence(activeSentenceElement) {
    const quoteId = this.quoteIdForSentence(activeSentenceElement);
    if (quoteId) {
      this.deleteQuote(quoteId);
      return;
    }

    this.selectSentenceAsQuote(activeSentenceElement);
  }

  quoteIdForSentence(sentenceElement) {
    if (!sentenceElement) {
      return null;
    }

    const sentenceText = this.sentenceQuoteText(sentenceElement);
    const matchingQuote = this.quotesValue.find(quote => quote.text === sentenceText);
    if (matchingQuote) {
      return matchingQuote.id;
    }

    const quoteElement = sentenceElement.querySelector('.quote');
    if (quoteElement) {
      return quoteElement.dataset.quoteId;
    }

    return null;
  }

  selectSentenceAsQuote(sentenceElement) {
    const sentenceText = this.sentenceQuoteText(sentenceElement);
    if (!sentenceText) {
      return;
    }

    this.saveQuote(sentenceText);
  }

  // Quote/highlight text excludes a leading Interviewer:/Respondent: label when present.
  sentenceQuoteText(sentenceElement) {
    const sentenceBodyElement = sentenceElement.querySelector('.sentence-body');
    if (sentenceBodyElement) {
      return sentenceBodyElement.textContent.trim();
    }

    return sentenceElement.textContent.trim();
  }

  sentenceElements() {
    return Array.from(this.transcriptTarget.querySelectorAll('.sentence'));
  }

  setActiveSentence(sentenceIndex, { showHighlight = false } = {}) {
    const sentenceElements = this.sentenceElements();
    if (sentenceElements.length === 0) {
      return;
    }

    if (showHighlight) {
      this.keyboardSentenceHighlightActive = true;
    }

    this.activeSentenceIndex = Math.max(0, Math.min(sentenceIndex, sentenceElements.length - 1));
    this.updateSentenceHighlight();
  }

  hideKeyboardSentenceHighlight() {
    this.keyboardSentenceHighlightActive = false;
    this.updateSentenceHighlight();
  }

  updateSentenceHighlight() {
    const sentenceElements = this.sentenceElements();
    const showActiveVisual = this.keyboardSentenceHighlightActive;

    sentenceElements.forEach((sentenceElement, index) => {
      const isActiveSentence = index === this.activeSentenceIndex;
      sentenceElement.classList.toggle('sentence-active', showActiveVisual && isActiveSentence);
      sentenceElement.setAttribute('aria-selected', showActiveVisual && isActiveSentence ? 'true' : 'false');
    });

    const activeSentenceElement = sentenceElements[this.activeSentenceIndex];
    if (showActiveVisual && activeSentenceElement) {
      this.transcriptTarget.setAttribute('aria-activedescendant', activeSentenceElement.id);
    } else {
      this.transcriptTarget.removeAttribute('aria-activedescendant');
    }
  }

  getSelectedText() {
    let selection = null;
    if (window.getSelection) {
      selection = window.getSelection();
    }

    if (document.selection) {
      selection = document.selection;
    }

    if (!selection) {
      return '';
    }

    return selection.toString().trim();
  }

  saveQuote(text) {
    if (!text || text === " ") {
      return
    }

    fetch('/quotes.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        quote: {
          text,
          quoteable_type: 'Entity::Game',
          quoteable_id: this.gameIdValue
        }
      })
    })
    .then(resp => resp.json())
    .then(data => {
      if (data.quotes && data.quotes.length > 0) {
        clearAlerts();
        createAlert('success', 'Quote saved');
        this.quotesValue = data.quotes;
        this.renderTranscript();
      } else if (data.text) {
        createAlert('danger', 'Quote must be longer than 4 words');
      }
    })
  }

  deleteQuote(quoteId) {
    fetch(`/quotes/${quoteId}.json`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(resp => resp.json())
    .then(data => {
      this.quotesValue = data.quotes;
      this.renderTranscript();
      createAlert('success', 'Quote removed');
    });
  }

  // Rebuild the transcript from the original plain text so sentence wrappers and quote
  // highlights stay in sync after adds and deletes.
  renderTranscript() {
    const transcriptText = this.originalTranscriptText;
    const sentenceRanges = this.getSentenceRanges(transcriptText);
    const quoteRanges = this.getQuoteRanges(transcriptText);

    let html = '';

    sentenceRanges.forEach((sentenceRange, sentenceIndex) => {
      const sentenceText = transcriptText.slice(sentenceRange.start, sentenceRange.end);
      const speakerLabelMatch = sentenceText.match(SPEAKER_LABEL_PATTERN);
      const speakerLabelLength = speakerLabelMatch ? speakerLabelMatch[0].length : 0;
      const bodyStart = sentenceRange.start + speakerLabelLength;

      let sentenceHtml = '';
      if (speakerLabelMatch) {
        // Keep the label in the transcript, but outside the underlined/quoted body
        sentenceHtml += `<span class="speaker-label">${this.escapeHtml(speakerLabelMatch[0])}</span>`;
      }

      let bodyHtml = '';
      let cursor = bodyStart;

      // Clip each quote to this sentence body so multi-sentence drag quotes still highlight
      const overlappingQuotes = quoteRanges
        .map(quoteRange => ({
          start: Math.max(quoteRange.start, bodyStart),
          end: Math.min(quoteRange.end, sentenceRange.end),
          id: quoteRange.id
        }))
        .filter(quoteRange => quoteRange.start < quoteRange.end)
        .sort((left, right) => left.start - right.start);

      overlappingQuotes.forEach(quoteRange => {
        if (cursor < quoteRange.start) {
          bodyHtml += this.escapeHtml(transcriptText.slice(cursor, quoteRange.start));
        }

        const quoteText = transcriptText.slice(quoteRange.start, quoteRange.end);
        bodyHtml += `<span class="quote" data-quote-id="${quoteRange.id}">${this.escapeHtml(quoteText)}</span>`;
        cursor = quoteRange.end;
      });

      if (cursor < sentenceRange.end) {
        bodyHtml += this.escapeHtml(transcriptText.slice(cursor, sentenceRange.end));
      }

      sentenceHtml += `<span class="sentence-body">${bodyHtml}</span>`;
      html += `<span class="sentence" id="game-recap-sentence-${sentenceIndex}" data-sentence-index="${sentenceIndex}" role="option">${sentenceHtml}</span>`;
    });

    this.transcriptTarget.innerHTML = html;
    this.updateSentenceHighlight();
  }

  getQuoteRanges(transcriptText) {
    const quoteRanges = [];

    this.quotesValue.forEach(quote => {
      const startIndex = transcriptText.indexOf(quote.text);
      if (startIndex < 0) {
        return;
      }

      quoteRanges.push({
        start: startIndex,
        end: startIndex + quote.text.length,
        id: quote.id
      });
    });

    return quoteRanges;
  }

  // Split the transcript into sentence character ranges. Periods inside abbreviations,
  // decimals, initials, ellipses, emails, and URLs are protected first so they do not
  // create false sentence boundaries.
  getSentenceRanges(transcriptText) {
    const protectedText = this.protectNonSentencePeriods(transcriptText);
    const sentenceRanges = [];
    let sentenceStartIndex = 0;

    // Sentence endings: . ! ? plus any trailing quotes/brackets, then whitespace or end of text
    const sentenceEndingPattern = /[.!?]+(?:['"`”’)\]]+)?(?=\s+|$)/g;
    let match;

    while ((match = sentenceEndingPattern.exec(protectedText)) !== null) {
      let sentenceEndIndex = match.index + match[0].length;

      // Keep trailing whitespace with this sentence so hover covers the natural gap
      while (sentenceEndIndex < protectedText.length && /\s/.test(protectedText[sentenceEndIndex])) {
        sentenceEndIndex += 1;
      }

      sentenceRanges.push({ start: sentenceStartIndex, end: sentenceEndIndex });
      sentenceStartIndex = sentenceEndIndex;
      sentenceEndingPattern.lastIndex = sentenceEndIndex;
    }

    if (sentenceStartIndex < transcriptText.length) {
      sentenceRanges.push({ start: sentenceStartIndex, end: transcriptText.length });
    }

    if (sentenceRanges.length === 0 && transcriptText.length > 0) {
      sentenceRanges.push({ start: 0, end: transcriptText.length });
    }

    return sentenceRanges;
  }

  protectNonSentencePeriods(transcriptText) {
    let protectedText = transcriptText;

    // Ellipsis: ...
    protectedText = protectedText.replace(/\.{2,}/g, (ellipsis) => PERIOD_PLACEHOLDER.repeat(ellipsis.length));

    // Emails and URLs — peel trailing sentence punctuation so "path. Next" still splits
    protectedText = protectedText.replace(/\S+@\S+\.\S+/g, (email) => this.protectPeriodsInMatch(email));
    protectedText = protectedText.replace(/https?:\/\/\S+/gi, (url) => this.protectPeriodsInMatch(url));
    protectedText = protectedText.replace(/www\.\S+/gi, (url) => this.protectPeriodsInMatch(url));

    // Multi-part abbreviations such as Ph.D. or U.S.A. (longest first).
    // A trailing period is only protected when the next word is lowercase
    // ("Ph.D. students"), so "Ph.D. She works" still ends a sentence.
    MULTI_PART_ABBREVIATIONS
      .slice()
      .sort((left, right) => right.length - left.length)
      .forEach(abbreviation => {
        const escapedAbbreviation = abbreviation.replace(/\./g, '\\.');
        const abbreviationPattern = new RegExp(`\\b${escapedAbbreviation}(\\.(?=\\s+[a-z]))?`, 'g');
        protectedText = protectedText.replace(abbreviationPattern, (match) => match.replace(/\./g, PERIOD_PLACEHOLDER));
      });

    // Title abbreviations such as Mr. or Dr. — the period is part of the title
    // and is almost always followed by a capitalized name, not a new sentence.
    TITLE_ABBREVIATIONS.forEach(abbreviation => {
      const abbreviationPattern = new RegExp(`\\b${abbreviation}\\.`, 'gi');
      protectedText = protectedText.replace(abbreviationPattern, (match) => match.replace(/\./g, PERIOD_PLACEHOLDER));
    });

    // Decimals such as 3.14
    protectedText = protectedText.replace(/(\d)\.(\d)/g, `$1${PERIOD_PLACEHOLDER}$2`);

    // Consecutive initials such as "A.B." that were not already covered above
    protectedText = protectedText.replace(/\b(?:[A-Za-z]\.){2,}/g, (initials) => initials.replace(/\./g, PERIOD_PLACEHOLDER));

    return protectedText;
  }

  // Protect periods inside a URL/email match, but leave trailing . ! ? ) ; , : alone
  // so they can still act as sentence endings.
  protectPeriodsInMatch(matchText) {
    let coreText = matchText;
    let trailingPunctuation = '';

    while (/[.,;:!?)]$/.test(coreText)) {
      trailingPunctuation = coreText.slice(-1) + trailingPunctuation;
      coreText = coreText.slice(0, -1);
    }

    return coreText.replace(/\./g, PERIOD_PLACEHOLDER) + trailingPunctuation;
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
